import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { App as AntApp, Button, Drawer, Form, Popconfirm, Space, Table, Typography } from 'antd';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDeleteProvider, useProviders, useSaveProvider } from '../api/hooks';
import type { Provider } from '../api/types';
import { extractErrorMessage } from '../api/client';
import { ProviderStatusTag } from '../utils/status';
import { mergeWebhookSecret, mergePollingByCategory, parseJsonText } from '../utils/format';
import ProviderFormFields from '../components/provider/ProviderFormFields';

const { Title, Link } = Typography;

export default function ProvidersPage() {
  const { message } = AntApp.useApp();
  const navigate = useNavigate();
  const list = useProviders();
  const save = useSaveProvider();
  const del = useDeleteProvider();

  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();

  const openCreate = () => {
    form.resetFields();
    form.setFieldsValue({
      status: 'active',
      authType: 'none',
      adapter: 'generic-http',
      timeoutMs: 30000,
      retryCount: 0,
    });
    setOpen(true);
  };

  const onSubmit = async () => {
    const v = await form.validateFields();
    const headers = parseJsonText(v.headers ?? '');
    const config = parseJsonText(v.config ?? '');
    const configValue = mergePollingByCategory(mergeWebhookSecret(config.value, v.webhookSecret), v.pollingRows);
    const payload: Partial<Provider> = {
      slug: v.slug,
      name: v.name,
      description: v.description,
      category: v.category,
      status: v.status,
      version: v.version,
      logo: v.logo,
      baseUrl: v.baseUrl,
      authType: v.authType,
      apiKey: v.apiKey,
      headers: headers.value as Record<string, unknown> | undefined,
      timeoutMs: v.timeoutMs,
      retryCount: v.retryCount,
      submitEndpoint: v.submitEndpoint,
      statusEndpoint: v.statusEndpoint,
      cancelEndpoint: v.cancelEndpoint,
      adapter: v.adapter,
      config: configValue,
    };
    try {
      const created = await save.mutateAsync({ data: payload });
      message.success('Đã tạo nhà cung cấp');
      setOpen(false);
      if (created?.id) navigate(`/providers/${created.id}`);
    } catch (e) {
      message.error(extractErrorMessage(e));
    }
  };

  const onDelete = async (id: string) => {
    try {
      await del.mutateAsync(id);
      message.success('Đã xóa nhà cung cấp');
    } catch (e) {
      message.error(extractErrorMessage(e));
    }
  };

  const columns = [
    { title: 'Slug', dataIndex: 'slug', key: 'slug' },
    {
      title: 'Tên',
      dataIndex: 'name',
      key: 'name',
      render: (v: string, r: Provider) => (
        <Link onClick={() => navigate(`/providers/${r.id}`)} style={{ fontWeight: 600 }}>
          {v}
        </Link>
      ),
    },
    {
      title: 'Danh mục',
      dataIndex: 'category',
      key: 'category',
      render: (v: string) => v || '—',
    },
    { title: 'Adapter', dataIndex: 'adapter', key: 'adapter' },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      render: (s: Provider['status']) => <ProviderStatusTag status={s} />,
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 160,
      render: (_: unknown, r: Provider) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => navigate(`/providers/${r.id}`)}>
            Chi tiết
          </Button>
          <Popconfirm
            title="Xóa nhà cung cấp này?"
            okText="Xóa"
            cancelText="Hủy"
            onConfirm={() => onDelete(r.id)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>
          Nhà cung cấp
        </Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Thêm nhà cung cấp
        </Button>
      </Space>

      {/* Không phân trang: hiện TOÀN BỘ nhà cung cấp trên một trang (danh sách ngắn, cần xem hết). */}
      <Table<Provider>
        rowKey="id"
        loading={list.isLoading}
        dataSource={list.data ?? []}
        columns={columns}
        pagination={false}
      />

      <Drawer
        title="Thêm nhà cung cấp"
        width={760}
        open={open}
        onClose={() => setOpen(false)}
        destroyOnClose
        extra={
          <Space>
            <Button onClick={() => setOpen(false)}>Hủy</Button>
            <Button type="primary" loading={save.isPending} onClick={onSubmit}>
              Lưu
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical">
          <ProviderFormFields />
        </Form>
      </Drawer>
    </div>
  );
}
